import { logger } from "../../services/logger.ts";
import React, { useState, useEffect } from 'react';
import { Market, MarketOption, DbMarketType, MarketStatus, MarketCategory, EngineType } from "../../types.ts";
import { supabase } from "../../lib/db.ts";
import { preserveLocalDateTime } from "../../utils/dateUtils.ts";
import { validateMarketCardImage, validateFeaturedImage } from "../../utils/imageUtils.ts";
import { fetchMarketCategories, Category as CategoryType } from "../../services/categoryService.ts";
import { useAuth } from "../../lib/auth.tsx"; // Import useAuth
import { toast } from 'react-hot-toast';
import {
  Loader2, AlertTriangle, Save, Plus, Trash2, Lock, Unlock,
  Tag as CategoryIcon, Image as ImageIcon, X,
  Clipboard, // Import Clipboard icon
  RefreshCw, // Import RefreshCw icon (or another for generate)
  ShieldCheck, // Import ShieldCheck icon
  RotateCcw, // Import RotateCcw for unreject icon
  Star, // Import Star icon for featured markets
  AlertCircle, // Import AlertCircle for warnings
  Info // Import Info icon for info message
} from 'lucide-react';
// Define expected props
interface MarketEditorProps {
  market: Market;
  marketId?: string;
  onMarketUpdate?: () => void; // Callback after successful update
  canEdit: boolean; // Add canEdit prop
}

// Add this interface at the top of the file
interface MarketFormData {
  title: string;
  description: string;
  market_type: DbMarketType;
  resolution_time?: string | undefined;
  status: MarketStatus;
  market_options: MarketOption[];
  fixed_fee?: number;
  is_private?: boolean;
  is_featured?: boolean;
  category?: MarketCategory;
  image_url?: string;
  short_code?: string | null;
  creator_fee_percent?: number;
  trading_end_time?: string | null;
  is_verified?: boolean;
  is_dynamic: boolean;
  featured_image_url?: string | null;
  engine_type: EngineType;
  lmsr_b: number | null;
}

function marketToFormData(m: Market): MarketFormData {
  return {
    title: m.title || '',
    description: m.description || '',
    market_type: m.market_type || 'fixed',
    resolution_time: m.resolution_time || undefined,
    status: m.status || 'pending',
    market_options: m.market_options || [],
    fixed_fee: m.fixed_fee || 0,
    is_private: m.is_private,
    is_featured: m.is_featured,
    category: m.category,
    image_url: m.image_url || undefined,
    short_code: m.short_code,
    creator_fee_percent: m.creator_fee_percent,
    trading_end_time: m.trading_end_time ?? null,
    is_verified: m.is_verified,
    is_dynamic: m.is_dynamic ?? true,
    featured_image_url: m.featured_image_url ?? null,
    engine_type: (m.engine_type as EngineType) ?? 'pool',
    lmsr_b: m.lmsr_b ?? null,
  };
}

// Define the function locally
const formatDateTimeLocal = (isoString: string | null | undefined): string => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const timezoneOffset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - timezoneOffset);
    return localDate.toISOString().slice(0, 16);
  } catch { return ''; }
};

// Define the available statuses for the dropdown
const marketStatuses: MarketStatus[] = ['active', 'ended', 'resolved', 'expired', 'cancelled', 'pending_approval', 'rejected'];

export function MarketEditor({ market, marketId, onMarketUpdate, canEdit }: MarketEditorProps) {
  const [formData, setFormData] = useState<MarketFormData>(() => marketToFormData(market));
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(market.image_url || null);
  const [imageFile, setImageFile] = useState<File | null>(null); // Store the actual file
  const [featuredImagePreview, setFeaturedImagePreview] = useState<string | null>(market.featured_image_url || null);
  const [featuredImageFile, setFeaturedImageFile] = useState<File | null>(null); // Store the actual file
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isUnrejecting, setIsUnrejecting] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false); // Added state for modal visibility
  const [rejectionReasonText, setRejectionReasonText] = useState(''); // Added state for rejection reason text
  const { user } = useAuth();
  const isAdmin = user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'superuser';

  // Store original market options for comparison
  const [, setOriginalMarketOptions] = useState<MarketOption[]>(market.market_options || []);

  // Remove the market status check from canEdit
  const canEditMarket = canEdit; // Only check admin status
  const isPendingApproval =
    market.status === 'pending_approval' || formData.status === 'pending_approval';

  // Visibility for verify/reject buttons (only for admins)
  const showVerifyButtons = isAdmin;

  // Fetch categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      const fetchedCategories = await fetchMarketCategories();
      setCategories(fetchedCategories);
      // Ensure the current market category exists in the fetched list
      if (!fetchedCategories.some(c => c.name === market.category)) {
        logger.warn('Market category not found in fetched list, defaulting...');
        // Optionally add it or default to the first category
      }
    };
    loadCategories();
  }, [market.category]);

  // Update formData if the market prop changes (e.g., after parent refetch)
  useEffect(() => {
    setFormData(marketToFormData(market));
    setOriginalMarketOptions(market.market_options || []);
    setImagePreview(market.image_url || null); // Update image preview too
    setImageFile(null); // Clear any pending file upload
  }, [market]);

  // --- Input Handlers ---
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    // Handle different input types
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (type === 'number') {
      const numValue = value === '' ? undefined : Number(value);
      setFormData((prev) => ({
        ...prev,
        [name]: numValue,
      }));
    } else if (type === 'datetime-local') {
      // Handle datetime inputs with timezone preservation
      const convertedValue = preserveLocalDateTime(value);
      setFormData(prev => ({
        ...prev,
        [name]: convertedValue
      }));
    } else {
      if (name === 'engine_type' && value === 'lmsr') {
        setFormData((prev) => {
          const presets = [500, 1500, 5000];
          const b = prev.lmsr_b != null && presets.includes(prev.lmsr_b) ? prev.lmsr_b : 500;
          return { ...prev, engine_type: value as EngineType, lmsr_b: b };
        });
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    }
    setUpdateError(null); // Clear error on input change
  };

  // --- Options Handlers --- 
  const handleOptionChange = (optionId: number, field: keyof MarketOption, value: string) => {
    setFormData((prev: MarketFormData) => ({
      ...prev,
      market_options: prev.market_options?.map((opt: MarketOption) => {
        if (opt.id === optionId) {
          return { ...opt, [field]: value };
        }
        return opt;
      }) || []
    }));
  };

  const handleAddOption = () => {
    // Check if we're at the maximum limit (let's say 20 options to prevent abuse)
    if (formData.market_options && formData.market_options.length >= 20) {
      toast.error("Maximum of 20 options allowed per market.");
      return;
    }

    const newOption: MarketOption = {
      id: -Date.now(), // Negative temporary ID to distinguish from database IDs
      market_id: marketId || '',
      name: '',
      status: 'active',
      trading_end_time: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    setFormData((prev: MarketFormData) => ({
      ...prev,
      market_options: [...(prev.market_options || []), newOption]
    }));
  };

  const handleRemoveOption = async (optionId: number) => {
    if (!marketId) {
      toast.error("Market ID is missing.");
      return;
    }

    // For new options (temporary IDs), just remove from local state
    if (optionId < 0) {
      setFormData((prev: MarketFormData) => ({
        ...prev,
        market_options: prev.market_options?.filter((opt: MarketOption) => opt.id !== optionId) || []
      }));
      return;
    }

    // For existing options (positive IDs), use the database function
    try {
      const { error } = await supabase.rpc('delete_market_option', {
        p_market_id: marketId,
        p_option_id_to_delete: optionId
      });

      if (error) throw error;

      // Remove from local state
      setFormData((prev: MarketFormData) => ({
        ...prev,
        market_options: prev.market_options?.filter((opt: MarketOption) => opt.id !== optionId) || []
      }));

      toast.success('Option deleted successfully!');
      
      // Refresh parent data to ensure consistency
      if (onMarketUpdate) onMarketUpdate();

    } catch (error: unknown) {
      logger.error("Error deleting option:", error);
      
      // Display the actual database error message, which is more informative
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("Could not delete option. Please try again.");
      }
    }
  };

  // --- Image Handlers ---
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (e.g., 5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    // Validate image dimensions
    const validation = await validateMarketCardImage(file);
    if (!validation.isValid) {
      // Show warnings but don't block upload
      validation.warnings.forEach(warning => toast(warning, { duration: 4000 }));
      if (validation.recommendations.length > 0) {
        toast(`Tip: ${validation.recommendations[0]}`, { duration: 3000 });
      }
    }

    // Store the file for uploading later
    setImageFile(file);

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      image_url: undefined
    }));
    setImagePreview(null);
    setImageFile(null);
  };

  // --- Featured Image Handlers ---
  const handleFeaturedImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('Image size must be less than 5MB');
      return;
    }

    // Validate image dimensions
    const validation = await validateFeaturedImage(file);
    if (!validation.isValid) {
      // Show warnings but don't block upload
      validation.warnings.forEach(warning => toast(warning, { duration: 4000 }));
      if (validation.recommendations.length > 0) {
        toast(`Tip: ${validation.recommendations[0]}`, { duration: 3000 });
      }
    }

    setFeaturedImageFile(file);
    const previewUrl = URL.createObjectURL(file);
    setFeaturedImagePreview(previewUrl);
  };

  const handleRemoveFeaturedImage = () => {
    setFormData(prev => ({
      ...prev,
      featured_image_url: undefined
    }));
    setFeaturedImagePreview(null);
    setFeaturedImageFile(null);
  };

  // Upload image to Supabase Storage
  const uploadImage = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `market-${marketId}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `market-covers/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('market-images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) {
      throw new Error(`Failed to upload image: ${uploadError.message}`);
    }

    const { data: { publicUrl } } = supabase.storage
      .from('market-images')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  // --- Short Code Handler ---
  const handleGenerateCode = async () => {
    if (!marketId) {
      toast.error("Market ID is missing.");
      return;
    }
    if (!formData.is_private) {
      toast.error("Short codes are only for private markets.");
      return;
    }

    setIsGeneratingCode(true);
    const toastId = toast.loading('Generating unique code...');

    try {
      const { data: generatedCode, error } = await supabase.rpc('generate_market_short_code', {
        p_market_id: marketId
      });

      if (error) {
        throw error;
      }

      if (generatedCode) {
        toast.success(`Generated code: ${generatedCode}`, { id: toastId });
        // Update local form state immediately
        setFormData(prev => ({
          ...prev,
          short_code: generatedCode
        }));
        // Optionally trigger parent update if needed, though local state handles UI
        // onMarketUpdate?.();
      } else {
         throw new Error("Failed to generate code (RPC returned no data).");
      }

    } catch (error: unknown) {
      logger.error("Error generating short code:", error);
      const message = error instanceof Error ? error.message : "Could not generate short code.";
      toast.error(`Error: ${message}`, { id: toastId });
    } finally {
      setIsGeneratingCode(false);
    }
  };

  // --- Copy to Clipboard Handler ---
  const handleCopyToClipboard = (text: string | null | undefined) => {
    if (!text) return;
    navigator.clipboard.writeText(text)
      .then(() => toast.success('Code copied to clipboard!'))
      .catch(err => {
        logger.error('Failed to copy text: ', err);
        toast.error('Failed to copy code.');
      });
  };

  // Tier map: UI selections correspond to the DB's approve_pending_lmsr_market
  // liquidity_tier enum. Keep these in sync with 20260418120000 migration.
  const LIQUIDITY_TIER_BY_B: Record<number, 'standard' | 'enhanced' | 'premium'> = {
    500: 'standard',
    1500: 'enhanced',
    5000: 'premium',
  };

  const extractErrorMessage = (err: unknown, fallback: string): string => {
    if (err instanceof Error) return err.message;
    const obj = (err ?? {}) as { message?: unknown };
    return typeof obj.message === 'string' ? obj.message : fallback;
  };

  // --- Verify Handler ---
  const handleVerifyMarket = async () => {
    if (!isAdmin) {
        toast.error("Only admins can verify markets.");
        return;
    }
    if (!marketId) {
      toast.error("Market ID is missing.");
      return;
    }
    if (formData.is_private) {
        toast.error("Private markets do not require verification.");
        return;
    }

    const engine = (
      formData.engine_type ??
      (market.engine_type as EngineType | undefined) ??
      'pool'
    ).toString().toLowerCase();

    // Share-priced (LMSR) pending markets must be approved via
    // approve_pending_lmsr_market so the Wayja platform wallet is debited b×ln(N)
    // as collateral. Tier is derived from the value selected in the Pricing
    // engine section of the editor (Standard/Enhanced/Premium).
    if (engine === 'lmsr' && (market.status === 'pending_approval' || formData.status === 'pending_approval')) {
      const b = Number(formData.lmsr_b ?? 0);
      const tier = LIQUIDITY_TIER_BY_B[b];
      if (!tier) {
        toast.error(
          'Pick a liquidity tier (Standard / Enhanced / Premium) in the Pricing engine section before approving this share-priced market.',
          { duration: 5000 },
        );
        return;
      }

      setIsVerifying(true);
      const toastId = toast.loading('Setting liquidity and activating market...');
      try {
        const { data, error } = await supabase.rpc('approve_pending_lmsr_market', {
          p_market_id: marketId,
          p_liquidity_tier: tier,
        });
        if (error) throw error;
        const payload = data as
          | { success?: boolean; error?: string; lmsr_b?: number; liquidity_tier?: string }
          | null;
        if (!payload || payload.success !== true) {
          throw new Error(payload?.error || 'Approval failed');
        }
        toast.success('Market approved and activated.', { id: toastId });
        setFormData(prev => ({
          ...prev,
          is_verified: true,
          status: 'active',
          lmsr_b: typeof payload.lmsr_b === 'number' ? payload.lmsr_b : prev.lmsr_b,
        }));
        market.is_verified = true;
        market.status = 'active';
        if (typeof payload.lmsr_b === 'number') market.lmsr_b = payload.lmsr_b;
        onMarketUpdate?.();
      } catch (err: unknown) {
        logger.error('Error approving LMSR market with liquidity:', err);
        toast.error(
          `Error: ${extractErrorMessage(err, 'Could not approve market.')}`,
          { id: toastId },
        );
      } finally {
        setIsVerifying(false);
      }
      return;
    }

    setIsVerifying(true);
    const toastId = toast.loading('Verifying market...');

    try {
      const { data, error } = await supabase.rpc('verify_market', {
        p_market_id: marketId
      });

      if (error) {
        throw error;
      }

      if (data === true) {
        toast.success('Market verified successfully!', { id: toastId });
        setFormData(prev => ({
          ...prev,
          is_verified: true,
          status: 'active'
        }));
        market.is_verified = true;
        market.status = 'active';
        onMarketUpdate?.();
      } else {
          throw new Error("Verification failed (RPC did not return success).");
      }

    } catch (error: unknown) {
      logger.error("Error verifying market:", error);
      toast.error(
        `Error: ${extractErrorMessage(error, 'Could not verify market.')}`,
        { id: toastId },
      );
    } finally {
      setIsVerifying(false);
    }
  };

  // --- Reject Handler --- (Modified to show modal first)
  const handleRejectMarketClick = () => {
    if (!isAdmin) {
        toast.error("Only admins can reject markets.");
        return;
    }
    if (!marketId) {
      toast.error("Market ID is missing.");
      return;
    }
    if (formData.is_private) {
        toast.error("Private markets do not require verification/rejection.");
        return;
    }
    if (market.status === 'rejected') {
        toast.error("This market is already rejected.");
        return;
    }
    setRejectionReasonText(''); // Clear previous reason
    setShowRejectionModal(true); // Show the modal
  };

  // --- Submit Rejection (Called from Modal) ---
  const submitRejection = async () => {
    if (!rejectionReasonText.trim()) {
        toast.error("Rejection reason cannot be empty.");
        return;
    }

    setShowRejectionModal(false); // Hide modal immediately
    setIsVerifying(true); // Use existing verifying state for loading
    const toastId = toast.loading('Rejecting market...');

    try {
      const { data, error } = await supabase.rpc('reject_market', {
        p_market_id: marketId,
        p_rejection_reason: rejectionReasonText 
      });

      if (error) {
        throw error;
      }

      if (data === true) {
        toast.success('Market rejected successfully!', { id: toastId });
        setFormData(prev => ({ 
          ...prev, 
          is_verified: false, 
          status: 'rejected',
          rejection_reason: rejectionReasonText 
        }));
        market.is_verified = false;
        market.status = 'rejected';
        market.rejection_reason = rejectionReasonText;
        onMarketUpdate?.();
      } else {
          throw new Error("Rejection failed (RPC did not return success).");
      }
    } catch (error: unknown) {
      logger.error("Error rejecting market:", error);
      const message = error instanceof Error ? error.message : "Could not reject market.";
      toast.error(`Error: ${message}`, { id: toastId });
    } finally {
      setIsVerifying(false);
    }
  };

  // --- Unreject Handler ---
  const handleUnrejectMarket = async () => {
    if (!isAdmin) {
        toast.error("Only admins can unreject markets.");
        return;
    }
    if (!marketId) {
      toast.error("Market ID is missing.");
      return;
    }
    if ((market.status || '').toLowerCase() !== 'rejected') {
        toast.error("This market is not in rejected state.");
        return;
    }

    setIsUnrejecting(true);
    const toastId = toast.loading('Unrejecting market...');

    try {
      const { data, error } = await supabase.rpc('unreject_market', {
        p_market_id: marketId
      });

      if (error) {
        throw error;
      }

      if (data === true) { // Check if RPC returned true
        toast.success('Market unrejected successfully!', { id: toastId });
        // Update local state
        setFormData(prev => ({ 
          ...prev, 
          status: 'pending_approval',
          is_verified: false 
        }));
        // Update market prop state
        market.status = 'pending_approval';
        market.is_verified = false;
        onMarketUpdate?.(); // Refresh parent data
      } else {
          throw new Error("Unrejection failed (RPC did not return success).");
      }

    } catch (error: unknown) {
      logger.error("Error unrejecting market:", error);
      const message = error instanceof Error ? error.message : "Could not unreject market.";
      toast.error(`Error: ${message}`, { id: toastId });
    } finally {
      setIsUnrejecting(false);
    }
  };

  // --- Save Handler --- 
  const handleSaveChanges = async () => {
    if (!marketId || !user) {
      toast.error("Cannot save: Missing market ID or user session.");
      return;
    }

    if (isPendingApproval) {
      toast.error('This market is pending approval. Use Approve & Activate or Reject — not Save Changes.');
      return;
    }

    // Validate that we have at least 2 options
    if (!formData.market_options || formData.market_options.length < 2) {
      toast.error("Market must have at least 2 options.");
      return;
    }

    // Validate that all options have names
    const optionsWithNames = formData.market_options.filter(opt => opt.name.trim());
    if (optionsWithNames.length < 2) {
      toast.error("All market options must have names.");
      return;
    }

    // Check for duplicate names
    const nameCounts = new Map<string, number>();
    for (const option of optionsWithNames) {
      const trimmedName = option.name.trim().toLowerCase();
      nameCounts.set(trimmedName, (nameCounts.get(trimmedName) || 0) + 1);
    }
    
    const duplicateNames = Array.from(nameCounts.entries())
      .filter(([, count]) => count > 1)
      .map(([name]) => name);
    
    if (duplicateNames.length > 0) {
      toast.error(`Please fix duplicate option names: ${duplicateNames.join(', ')}`);
      return;
    }

    if (formData.status === 'active' && formData.engine_type === 'lmsr') {
      const lmsrPresetB = [500, 1500, 5000];
      if (formData.lmsr_b == null || !lmsrPresetB.includes(formData.lmsr_b)) {
        toast.error('Share-priced markets need a liquidity tier: Standard (500), Enhanced (1500), or Premium (5000).');
        return;
      }
    }

    setIsUpdating(true);
    const originalStatus = market.status;
    const newStatus = formData.status;

    try {
      // Upload image if a new one was selected
      let imageUrl = formData.image_url;
      if (imageFile) {
        toast.loading('Uploading image...', { id: 'image-upload' });
        try {
          imageUrl = await uploadImage(imageFile);
          toast.success('Image uploaded successfully!', { id: 'image-upload' });
          // Update formData with the new image URL
          setFormData(prev => ({
            ...prev,
            image_url: imageUrl
          }));
          // Clear the file state
          setImageFile(null);
          // Update preview to use the uploaded URL
          setImagePreview(imageUrl);
        } catch (error) {
          logger.error('Failed to upload image:', error);
          toast.error(error instanceof Error ? error.message : 'Failed to upload image', { id: 'image-upload' });
          throw error;
        }
      }

      // Upload featured image if a new one was selected
      let featuredImageUrl = formData.featured_image_url;
      if (featuredImageFile) {
        toast.loading('Uploading featured image...', { id: 'featured-image-upload' });
        try {
          featuredImageUrl = await uploadImage(featuredImageFile);
          toast.success('Featured image uploaded successfully!', { id: 'featured-image-upload' });
          // Update formData with the new featured image URL
          setFormData(prev => ({
            ...prev,
            featured_image_url: featuredImageUrl
          }));
          // Clear the file state
          setFeaturedImageFile(null);
          // Update preview to use the uploaded URL
          setFeaturedImagePreview(featuredImageUrl);
        } catch (error) {
          logger.error('Failed to upload featured image:', error);
          toast.error(error instanceof Error ? error.message : 'Failed to upload featured image', { id: 'featured-image-upload' });
          throw error;
        }
      }

      // Always call the main update function, as other fields might have changed.
      const { error: detailsError } = await supabase.rpc('update_market_details', {
        p_category: formData.category,
        p_creator_fee_percent: formData.creator_fee_percent,
        p_description: formData.description,
        p_featured_image_url: featuredImageUrl,
        p_fixed_fee: formData.fixed_fee,
        p_image_url: imageUrl,
        p_is_dynamic: formData.is_dynamic,
        p_is_featured: formData.is_featured,
        p_is_private: formData.is_private,
        p_market_id: marketId,
        p_market_type: formData.market_type,
        p_resolution_time: formData.resolution_time?.trim() ? formData.resolution_time : null,
        p_title: formData.title,
        p_trading_end_time: null
      });

      if (detailsError) throw detailsError;

      const { error: engineError } = await supabase
        .from('markets')
        .update({
          engine_type: formData.engine_type,
          lmsr_b: formData.engine_type === 'lmsr' ? formData.lmsr_b : null,
        })
        .eq('id', marketId);

      if (engineError) throw engineError;

      // Handle new options (negative IDs) - insert them
      const newOptions = optionsWithNames.filter(opt => opt.id < 0);
      if (newOptions.length > 0) {
        const insertData = newOptions.map(option => ({
          market_id: marketId,
          name: option.name.trim(),
          trading_end_time: null,
          status: option.status || 'active'
        }));

        const { error: insertError } = await supabase
          .from('market_options')
          .insert(insertData);

        if (insertError) throw insertError;
      }

      // Handle existing options (positive IDs) - update them
      const existingOptions = optionsWithNames.filter(opt => opt.id > 0);
      for (const option of existingOptions) {
        const { error: updateError } = await supabase
          .from('market_options')
          .update({
            name: option.name.trim(),
            trading_end_time: null,
            status: option.status || 'active',
            updated_at: new Date().toISOString()
          })
          .eq('id', option.id);

        if (updateError) throw updateError;
      }

      // If status was changed by an admin, call the specific function for that.
      if (originalStatus !== newStatus && isAdmin) {
        toast.loading('Applying status change...', { id: 'status-toast' });
        const { error: statusError } = await supabase.rpc('admin_set_market_status', {
          p_market_id: marketId,
          p_status: newStatus
        });
        if (statusError) throw statusError;
        toast.success('Status updated!', { id: 'status-toast' });
      }
      
      toast.success('Market updated successfully!');
      
      // Update local state to reflect the saved options
      setFormData(prev => ({
        ...prev,
        market_options: optionsWithNames
      }));

      // Update the original options for future comparisons
      setOriginalMarketOptions(optionsWithNames);
      
      if (onMarketUpdate) onMarketUpdate();

    } catch (err) {
      const error = err as { message: string };
      logger.error('Failed to update market:', error);
      toast.error(error.message || 'An error occurred while saving.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Helper function to check if an option name is duplicate
  const isDuplicateOptionName = (currentId: number, name: string): boolean => {
    if (!name.trim()) return false;
    
    const trimmedName = name.trim().toLowerCase();
    return formData.market_options?.some((opt: MarketOption) => 
      opt.id !== currentId && opt.name.trim().toLowerCase() === trimmedName
    ) || false;
  };

  // Helper function to get all duplicate names for display
  const getDuplicateOptionNames = (): string[] => {
    const nameCounts = new Map<string, number>();
    const duplicates: string[] = [];
    
    formData.market_options?.forEach(option => {
      const trimmedName = option.name.trim().toLowerCase();
      if (trimmedName) {
        const count = nameCounts.get(trimmedName) || 0;
        nameCounts.set(trimmedName, count + 1);
        if (count === 1) { // This is the second occurrence, so it's a duplicate
          duplicates.push(option.name.trim());
        }
      }
    });
    
    return duplicates;
  };

  return (
    <div className="space-y-8 bg-white rounded-lg shadow p-6"> {/* Added bg-white and shadow */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Edit Market</h2>
        {!canEditMarket && (
          <div className="text-sm text-gray-500">
            <Info className="w-4 h-4 inline mr-1" />
            Only admins can edit markets
          </div>
        )}
      </div>
      {/* Section 1: Core Information */}
      <section>
        <h3 className="text-lg font-medium mb-4 text-gray-800">Core Information</h3>
        <div className="space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">Market Title</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title || ''}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md bg-white disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!canEditMarket}
            />
          </div>
          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description/Rules</label>
            <textarea
              id="description"
              name="description"
              rows={4}
              value={formData.description || ''}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md bg-white disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!canEditMarket}
            />
          </div>
          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700">Category</label>
            <div className="relative">
              <CategoryIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
              <select
                id="category"
                name="category"
                value={formData.category || ''}
                onChange={handleInputChange}
                className="w-full pl-10 p-2 border border-gray-300 rounded-md bg-white appearance-none disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={categories.length === 0 || !canEditMarket}
              >
                <option value="" disabled>Select Category...</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>
          {/* Image Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Market Image</label>
            <div className={`space-y-4 ${!canEditMarket ? 'opacity-50' : ''}`}> {/* Opacity for whole section */} 
              {/* Image Preview */}
              {imagePreview && (
                <div className="relative w-full max-w-md">
                  <img
                    src={imagePreview}
                    alt="Market preview"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  {canEditMarket && ( /* Only show remove button if editable */
                     <button
                       type="button"
                       onClick={handleRemoveImage}
                       className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                       aria-label="Remove image"
                       disabled={!canEditMarket}
                     >
                       <X className="w-4 h-4" />
                     </button>
                  )}
                </div>
              )}
              {/* Image Upload */}
              <div className="flex items-center gap-4">
                <label className={`flex items-center gap-2 px-4 py-2 text-white rounded-md transition-colors ${canEditMarket ? 'bg-indigo-600 hover:bg-indigo-700 cursor-pointer' : 'bg-gray-400 cursor-not-allowed'}`}> {/* Conditional styling/cursor */}
                  <ImageIcon className="w-5 h-5" />
                  <span>{imagePreview ? 'Change Image' : 'Upload Image'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    disabled={isGeneratingCode || !canEditMarket}
                  />
                </label>
                {isGeneratingCode && (
                   <div className="flex items-center gap-2 text-gray-600">
                     <Loader2 className="w-4 h-4 animate-spin" />
                     <span>Generating...</span>
                   </div>
                )}
              </div>
              <p className="text-sm text-gray-500">
                Recommended size: 1200x630px. Max file size: 5MB.
              </p>
            </div>
          </div>
          {/* Featured Image Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Featured image (optional)</label>
            <div className={`space-y-4 ${!canEditMarket ? 'opacity-50' : ''}`}> {/* Opacity for whole section */}
              {/* Featured Image Preview */}
              {featuredImagePreview && (
                <div className="relative w-full max-w-md">
                  <img
                    src={featuredImagePreview}
                    alt="Featured preview"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  {canEditMarket && ( /* Only show remove button if editable */
                     <button
                       type="button"
                       onClick={handleRemoveFeaturedImage}
                       className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                       aria-label="Remove featured image"
                       disabled={!canEditMarket}
                     >
                       <X className="w-4 h-4" />
                     </button>
                  )}
                </div>
              )}
              {/* Featured Image Upload */}
              <div className="flex items-center gap-4">
                <label className={`flex items-center gap-2 px-4 py-2 text-white rounded-md transition-colors ${canEditMarket ? 'bg-indigo-600 hover:bg-indigo-700 cursor-pointer' : 'bg-gray-400 cursor-not-allowed'}`}> {/* Conditional styling/cursor */}
                  <ImageIcon className="w-5 h-5" />
                  <span>{featuredImagePreview ? 'Change Featured Image' : 'Upload Featured Image'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFeaturedImageChange}
                    className="hidden"
                    disabled={isGeneratingCode || !canEditMarket}
                  />
                </label>
                {isGeneratingCode && (
                   <div className="flex items-center gap-2 text-gray-600">
                     <Loader2 className="w-4 h-4 animate-spin" />
                     <span>Generating...</span>
                   </div>
                )}
              </div>
              <p className="text-sm text-gray-500">
                Optional wide hero (e.g. 1200×400). If omitted, the standard market image is used. Max file size: 5MB.
              </p>
            </div>
          </div>
          {/* Market Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Market Status</label>
            <select
              id="status"
              name="status"
              value={formData.status || ''}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md bg-white disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!isAdmin}
            >
              {marketStatuses.map((status) => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                </option>
              ))}
            </select>
            {!isAdmin && (
              <p className="text-xs text-gray-500 mt-1">Status is managed automatically.</p>
            )}
          </div>
        </div>
      </section>

      {/* Section 4: Access */}
      <section className="pt-6 border-t border-gray-200">
         <h3 className="text-lg font-medium mb-4 text-gray-800">Access</h3>
         <div className="space-y-4"> {/* Reduced outer spacing */} 
             {/* Combined Privacy Toggle and Code Section */} 
             <div className="p-4 border border-gray-200 rounded-lg bg-gray-50/50">
               {/* Privacy Toggle with Description */} 
               <div className="mb-3"> {/* Container for toggle + description */} 
                 <label htmlFor="is_private" className="flex items-center cursor-pointer mb-1.5"> {/* Added bottom margin */} 
                   <input
                       type="checkbox"
                       id="is_private"
                       name="is_private"
                       checked={formData.is_private || false}
                       onChange={handleInputChange}
                       className="form-checkbox h-5 w-5 text-indigo-600 rounded mr-2 disabled:opacity-50 disabled:cursor-not-allowed"
                       disabled={!canEditMarket}
                   />
                   {formData.is_private ? <Lock className="w-5 h-5 text-gray-600 mr-1" /> : <Unlock className="w-5 h-5 text-gray-600 mr-1" />}
                   <span className={`text-sm font-medium text-gray-700 ${!canEditMarket ? 'opacity-50' : ''}`}>
                     {formData.is_private ? 'Private Market (Invite Only)' : 'Public Market (Open to Everyone)'}
                   </span>
                 </label>
                 {/* Conditional Description */} 
                 <div className={`pl-7 text-xs text-gray-600 space-y-1 ${!canEditMarket ? 'opacity-50' : ''}`}> {/* Indent description */} 
                   {formData.is_private ? (
                     <p>🔒 Private markets are invite-only. Only users with the link can access.</p>
                   ) : (
                     <p>🌍 Public markets are open to everyone on Wayja. Visible on homepage.</p>
                   )}
                 </div>
               </div>

               {/* Short Code (Conditional on is_private) */} 
               {formData.is_private && (
                 <div className={`space-y-2 pl-7 ${!canEditMarket ? 'opacity-50' : ''}`}> {/* Indent code section, adjusted spacing */} 
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Private Access Code
                   </label>
                   {formData.short_code ? (
                     <div className="flex items-center gap-2 p-2 border border-gray-300 rounded-md bg-white">
                       <span className="font-mono text-sm text-gray-800 flex-grow">
                         {formData.short_code}
                       </span>
                       <button
                         type="button"
                         onClick={() => handleCopyToClipboard(formData.short_code)}
                         className="p-1.5 text-gray-500 rounded-md hover:bg-gray-100 hover:text-indigo-600 transition-colors"
                         title="Copy code"
                       >
                         <Clipboard className="w-4 h-4" />
                       </button>
                     </div>
                   ) : (
                     <button
                       type="button"
                       onClick={handleGenerateCode}
                       disabled={isGeneratingCode || !canEditMarket}
                       className="flex items-center gap-2 px-3 py-1.5 text-sm bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                     >
                       {isGeneratingCode ? (
                         <Loader2 className="w-4 h-4 animate-spin" />
                       ) : (
                         <RefreshCw className="w-4 h-4" /> 
                       )}
                       <span>{isGeneratingCode ? 'Generating...' : 'Generate Code'}</span>
                     </button>
                   )}
                   <p className="text-xs text-gray-500 pt-1">
                      Users need this code to find and access this private market.
                   </p>
                 </div>
               )}
             </div>

             {/* Featured Market Toggle (Only visible to admins) */}
             {isAdmin && (
               <div className="p-4 border border-gray-200 rounded-lg bg-gray-50/50">
                 <label htmlFor="is_featured" className="flex items-center cursor-pointer mb-1.5">
                   <input
                     type="checkbox"
                     id="is_featured"
                     name="is_featured"
                     checked={formData.is_featured || false}
                     onChange={handleInputChange}
                     className="form-checkbox h-5 w-5 text-indigo-600 rounded mr-2 disabled:opacity-50 disabled:cursor-not-allowed"
                     disabled={!canEditMarket || !isAdmin}
                   />
                   <Star className={`w-5 h-5 mr-1 ${formData.is_featured ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`} />
                   <span className={`text-sm font-medium text-gray-700 ${!canEditMarket ? 'opacity-50' : ''}`}>
                     Featured Market
                   </span>
                 </label>
                 <div className={`pl-7 text-xs text-gray-600 ${!canEditMarket ? 'opacity-50' : ''}`}>
                   <p>⭐ Featured markets are highlighted on the homepage. The standard market image is used unless you add an optional wide hero above.</p>
                 </div>
               </div>
             )}

            {/* Creator fee moved to Revenue & Fees (platform defaults + per-market override). */}
         </div>
      </section>
      {/* Section 2: Market Structure */}
      <section className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-medium mb-4 text-gray-800">Market Structure</h3>
        <div className="space-y-6">
           {/* Market Type - Changed to Radio Buttons */}
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-2">Market Type</label>
             <div className="flex items-center space-x-6">
               <div className={`${!canEditMarket ? 'opacity-50' : ''}`}> {/* Group label and description */} 
                 <label className={`flex items-center cursor-pointer ${!canEditMarket ? 'cursor-not-allowed' : ''}`}>
                   <input
                     type="radio"
                     name="market_type"
                     value="fixed"
                     checked={formData.market_type === 'fixed'}
                     onChange={handleInputChange}
                     className="form-radio h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500 disabled:opacity-70 disabled:cursor-not-allowed"
                     disabled={!canEditMarket}
                   />
                   <span className="ml-2 text-sm text-gray-700">Standard Market</span>
                 </label>
                 <p className="ml-6 text-xs text-gray-500 mt-1">Trading continues until resolution.</p>
               </div>
               <div className={`${!canEditMarket ? 'opacity-50' : ''}`}> {/* Group label and description */} 
                 <label className={`flex items-center cursor-pointer ${!canEditMarket ? 'cursor-not-allowed' : ''}`}>
                   <input
                     type="radio"
                     name="market_type"
                     value="knockout"
                     checked={formData.market_type === 'knockout'}
                     onChange={handleInputChange}
                     className="form-radio h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500 disabled:opacity-70 disabled:cursor-not-allowed"
                     disabled={!canEditMarket}
                   />
                   <span className="ml-2 text-sm text-gray-700">Knockout Market</span>
                 </label>
                 <p className="ml-6 text-xs text-gray-500 mt-1">Options can be eliminated over time until resolution.</p>
               </div>
             </div>
           </div>
           {/* Market Options Editor */} 
           <div className={`${!canEditMarket ? 'opacity-50' : ''}`}>
             <label className="block text-sm font-medium text-gray-700 mb-2">Market Options</label>
             
             {/* Duplicate names warning */}
             {(() => {
               const duplicateNames = getDuplicateOptionNames();
               
               if (duplicateNames.length > 0) {
                 return (
                   <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
                     <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                     <div>
                       <p className="text-red-700 font-medium">Duplicate option names detected</p>
                       <p className="text-red-600 text-sm">
                         The following names are duplicated: {duplicateNames.map(name => `"${name}"`).join(', ')}. 
                         Each option must have a unique name.
                       </p>
                     </div>
                   </div>
                 );
               }
               return null;
             })()}
             
             <div className="space-y-3">
                 {formData.market_options?.map((option) => (
                     <div key={option.id} className="flex flex-col sm:flex-row items-start gap-2 p-2 border border-gray-200 rounded-md bg-gray-50">
                         <div className="flex-grow w-full">
                           <input
                             type="text"
                             value={option.name}
                             onChange={(e) => handleOptionChange(option.id, 'name', e.target.value)}
                             placeholder="Option Name"
                             className={`w-full p-1 border rounded-md bg-white disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                               isDuplicateOptionName(option.id, option.name) 
                                 ? 'border-red-500 focus:ring-red-500' 
                                 : 'border-gray-300'
                             }`}
                             disabled={!canEditMarket}
                           />
                           {isDuplicateOptionName(option.id, option.name) && (
                             <p className="text-xs text-red-500 mt-1">
                               This name already exists in the market. Please use a unique name.
                             </p>
                           )}
                           {option.name.trim() && !isDuplicateOptionName(option.id, option.name) && (
                             <p className="text-xs text-green-500 mt-1">
                               ✓ Unique name
                             </p>
                           )}
                         </div>
                         {/* Remove Button - Only show if editable */} 
                         {canEditMarket && (
                            <button
                              type="button"
                              onClick={() => handleRemoveOption(option.id)}
                              className="p-1 text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 mt-1 sm:mt-0"
                              aria-label="Remove option"
                              disabled={formData.market_options?.length <= 2 || !canEditMarket} 
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                         )}
                     </div>
                 ))}
             </div>
             {/* Add Option Button - Only show if editable */} 
             {canEditMarket && (
                <button
                  type="button"
                  onClick={handleAddOption}
                  className="mt-3 flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
                  disabled={!canEditMarket}
                >
                  <Plus className="w-4 h-4" />
                  Add Option
                </button>
             )}
           </div>
        </div>
      </section>

      {/* Pricing engine — admin sets share-priced (per-share) tier before activating user-submitted markets */}
      <section className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-medium mb-2 text-gray-800">Pricing engine</h3>
        <p className="text-xs text-gray-500 mb-4">
          User-submitted markets start as <strong>pool</strong> until you switch to <strong>share-priced</strong> and set liquidity (b). Wayja provides the backing; liquidity returns at resolution go to the platform wallet.
        </p>
        <div className={`space-y-4 ${!canEditMarket ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="engine_type"
                value="pool"
                checked={formData.engine_type === 'pool'}
                onChange={handleInputChange}
                className="form-radio h-4 w-4 text-indigo-600 border-gray-300"
                disabled={!canEditMarket}
              />
              <span className="ml-2 text-sm text-gray-700">Pool (legacy odds)</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="engine_type"
                value="lmsr"
                checked={formData.engine_type === 'lmsr'}
                onChange={handleInputChange}
                className="form-radio h-4 w-4 text-indigo-600 border-gray-300"
                disabled={!canEditMarket}
              />
              <span className="ml-2 text-sm text-gray-700">Share-priced (per-share, live price)</span>
            </label>
          </div>
          {formData.engine_type === 'lmsr' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Liquidity tier</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {(
                    [
                      { b: 500, label: 'Standard', description: 'Low liquidity, wider price swings' },
                      { b: 1500, label: 'Enhanced', description: 'Medium liquidity, moderate stability' },
                      { b: 5000, label: 'Premium', description: 'High liquidity, tight spreads' },
                    ] as const
                  ).map(({ b, label, description }) => (
                    <button
                      key={b}
                      type="button"
                      disabled={!canEditMarket}
                      onClick={() => setFormData((prev) => ({ ...prev, lmsr_b: b }))}
                      className={`text-left border rounded-lg p-3 transition-colors ${
                        formData.lmsr_b === b
                          ? 'border-indigo-600 bg-indigo-50 ring-1 ring-indigo-600'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <p className="text-sm font-semibold text-gray-900">{label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
                <p className="font-medium text-amber-800">LMSR collateral (Wayja-funded)</p>
                <p className="text-amber-700 mt-1">
                  {`About R${parseFloat(
                    (
                      (formData.lmsr_b ?? 500) *
                      Math.log(Math.max(formData.market_options?.length ?? 2, 2))
                    ).toFixed(2),
                  )} reference for this depth and number of outcomes (b × ln(N)). Wayja fronts liquidity; unused collateral returns to the platform wallet at resolution after settlement.`}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Section 3: Timing */}
      <section className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-medium mb-4 text-gray-800">Timing</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Resolution Time */} 
            <div>
              <label htmlFor="resolution_time" className="block text-sm font-medium text-gray-700 mb-1">
                Market resolution (optional)
              </label>
              <input
                type="datetime-local"
                id="resolution_time"
                name="resolution_time"
                value={formatDateTimeLocal(formData.resolution_time)}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-md bg-white disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={!canEditMarket}
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty for open-ended topics (shows as TBA publicly). Required if you add a trading end time elsewhere.
              </p>
            </div>
        </div>
      </section>



      {/* Rejection Reason Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md mx-auto">
            <h4 className="text-lg font-medium mb-4 text-gray-800">Reject Market</h4>
            <p className="text-sm text-gray-600 mb-1">Please provide a reason for rejecting this market:</p>
            <textarea
              value={rejectionReasonText}
              onChange={(e) => setRejectionReasonText(e.target.value)}
              rows={4}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter reason here..."
            />
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowRejectionModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitRejection}
                disabled={!rejectionReasonText.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Reject Market
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons Area */}
      <div className="pt-8 border-t border-gray-200 flex flex-wrap justify-between items-center gap-4">
        {/* Left side: Verify/Reject/Unreject Buttons */}
        {showVerifyButtons && (
          <div className="flex gap-2">
            {(market.status || '').toLowerCase() !== 'rejected' ? (
              <>
                <button
                  type="button"
                  onClick={handleVerifyMarket}
                  disabled={isVerifying || isUpdating || isUnrejecting || !isAdmin || market.status !== 'pending_approval'}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                  title={formData.engine_type === 'lmsr'
                    ? 'Pick a liquidity tier in the Pricing engine section, then approve.'
                    : 'Verify this market'}
                >
                  {isVerifying && market.status !== 'rejected' ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
                  {isVerifying && market.status !== 'rejected'
                    ? 'Verifying...'
                    : formData.engine_type === 'lmsr'
                      ? 'Approve & activate'
                      : 'Verify Market'}
                </button>
                <button
                  type="button"
                  onClick={handleRejectMarketClick}
                  disabled={isVerifying || isUpdating || isUnrejecting || !isAdmin || market.status === 'rejected'}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isVerifying && market.status === 'pending_approval' ? <Loader2 className="w-5 h-5 animate-spin" /> : <X className="w-5 h-5" />}
                  {'Reject Market'}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleUnrejectMarket}
                disabled={isVerifying || isUpdating || isUnrejecting || !isAdmin}
                className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isUnrejecting ? <Loader2 className="w-5 h-5 animate-spin" /> : <RotateCcw className="w-5 h-5" />}
                {isUnrejecting ? 'Unrejecting...' : 'Unreject Market'}
              </button>
            )}
          </div>
        )}

        {/* Right side: Save Button, Error Display, and Status Messages */}
        <div className="flex items-center gap-4">
          {/* Error Display */}
          {updateError && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> {updateError}
            </p>
          )}
          {/* Status Messages */}
          {!canEditMarket && (
            <p className="text-sm text-gray-500">
              {market.status === 'rejected' ? `This market was rejected. Reason: ${market.rejection_reason || 'Not provided'}` :
               market.status === 'ended' ? 'This market has ended and cannot be edited.' :
               market.status === 'expired' ? 'This market has expired and cannot be edited.' :
               market.status === 'cancelled' ? 'This market has been cancelled and cannot be edited.' :
               market.status === 'resolved' ? 'This market has been resolved and cannot be edited.' :
               'Editing is disabled for this market.'}
            </p>
          )}
          {isPendingApproval && canEditMarket && (
            <p className="text-sm text-gray-600 max-w-md">
              Use <strong>Approve &amp; Activate</strong> (pick liquidity) or <strong>Reject</strong>. Save Changes is not available while pending.
            </p>
          )}
          {/* Save Button - hidden for pending_approval (approve/reject flow only) */}
          {canEditMarket && !isPendingApproval && (
            <button
              type="button"
              onClick={handleSaveChanges}
              disabled={isUpdating || isVerifying}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isUpdating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
} 